/* eslint-disable import/no-cycle */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, Input, OnChanges,
  OnInit, SimpleChanges,
} from '@angular/core';
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { ResumeContentSelectionModalComponent } from '../content-selection-modal/content-selection-modal.component';
import { ResumeComponent } from '../resume.component';
import { ResumeSection } from '../section/section.model';
import { SectionDataAlertService } from '../section/section-data-alert.service';

/**
 * Abstract component for all Resume sub-sections, to handle the different modes, linked content, and accordion
 * behavior.
 */
@Component({
  selector: 'bulb-resume-sub-section',
  templateUrl: './sub-section.component.html',
  standalone: false,
})
export class ResumeSubSectionComponent implements OnChanges, OnInit {
  /**
   * Whether or not the resume sub-section could be deleted.
   */
  @Input() allowSubSectionDelete : boolean = false;

  /**
   * Whether the sub-section should be displaying edit mode vs readonly/view mode.
   */
  @Input() editMode: boolean;

  /**
   * Reference to the parent resume component.
   */
  @Input() resumeComponent: ResumeComponent;

  /**
   * The resume section being rendered.
   */
  @Input() resumeSection : ResumeSection;

  /**
   * The units for display in the portfolio content section
   */
  @Input() units?: any = null;

  /**
   * Whether the sub section has only cards to display in view mode.
   */
  @Input() isOnlyPortfolioContent : boolean = false;

  /**
   * Whether the sub section has cards or not, to determine of the view mode can be expanded.
   * @private
   */
  public hasPortfolioContent : boolean = false;

  /**
   * Whether the tab (the view mode) is currently expanded to show attached units.
   * @private
   */
  public isTabOpen: boolean = false;

  /**
   * Default constructor.
   *
   * @param aeroDialogService
   * @param sectionDataAlertService
   */
  constructor(
    private aeroDialogService : AeroDialogService,
    private sectionDataAlertService : SectionDataAlertService,
  ) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   */
  ngOnInit() {
    if (this.units && this.units.length > 0) {
      this.hasPortfolioContent = true;

      // Open all portfolio content sub-section
      this.isTabOpen = true;
    }
  }

  /**
   * A lifecycle hook that is called when any data-bound property of a component changes.
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) : void {
    if (changes.units) {
      if (changes.units.currentValue) {
        this.hasPortfolioContent = changes.units.currentValue.length > 0;
      }
    }
    if (changes.editMode) {
      if (!changes.editMode.currentValue
        && this.hasPortfolioContent
        && changes.editMode.previousValue !== undefined) {
        // Ending edit mode.  If saved with portfolioContent, then open up the accordion tab
        // The test for previousValue !== undefined makes sure this doesn't fire when the component is
        // originally initialized.
        this.isTabOpen = true;
      }
    }
  }

  /**
   * Handle adding the user's potential portfolio content to about-me section.
   *
   * @param subSectionType
   * @param subSectionIndex
   */
  public addPortfolioContent(subSectionType : InstanceType< any >, subSectionIndex ?: number) : void {
    const contentSelectionModalOptions : any = {
      width: '100%',
      height: '100%',
      closable: false,
      styleClass: 'resume-content-selection-modal aero-dialog',
    };
    const dialog = this.aeroDialogService.show(
      ResumeContentSelectionModalComponent,
      contentSelectionModalOptions,
    );
    const subscription = dialog.onClose.subscribe((units : any) => {
      subscription.unsubscribe();
      if (units) {
        units.forEach((unit) => {
          // eslint-disable-next-line no-param-reassign
          // eslint-disable-next-line no-plusplus
          subSectionType.numberOfSelectedContent++;
          if (typeof (subSectionIndex) === 'undefined') {
            subSectionType.portfolioContent.push(unit);
          } else {
            const subSectionCloneType : InstanceType<any> = (subSectionType.experienceSubSectionClone)
              ? subSectionType.experienceSubSectionClone : subSectionType.educationSubSectionClone;
            subSectionCloneType[subSectionIndex].portfolioContent.push(unit);
          }
        });
        /**
         * This is needed due to how / when Angular detection cycle runs and is triggered.
         *
         * Angular change detection only checks object identity, not object content.
         * Therefore, inserts or removals are not detected.
         *
         * This makes the sub-section component ngOnChanges detect the portfolioContent property value change.
         */
        if (typeof (subSectionIndex) === 'undefined') {
          // eslint-disable-next-line no-param-reassign
          subSectionType.portfolioContent = subSectionType.portfolioContent.slice();
          this.sectionDataAlertService.handleSaveButtonForAddRemovePortfolioContent(subSectionType);
        } else {
          const subSectionCloneType : InstanceType<any> = (subSectionType.experienceSubSectionClone)
            ? subSectionType.experienceSubSectionClone : subSectionType.educationSubSectionClone;
          subSectionCloneType[subSectionIndex].portfolioContent = subSectionCloneType[subSectionIndex].portfolioContent
            .slice();
          this.sectionDataAlertService.handleSaveButtonForAddRemovePortfolioContent(subSectionType, subSectionIndex);
        }
        // Update portfolio content modified map :
        this.sectionDataAlertService.updatePortfolioContentModifiedMap(subSectionType);
      }
    });
  }

  /**
   * Helper method to update the portoFolioContent object identity so that the Angular detection cycle could detect
   * the object change after the removal of any portfolio content.
   *
   * @param subSectionType type of resume sub section -- about me, experience, education, custom.
   * @param subSectionIndex
   */
  public removePortfolioContent(subSectionType : InstanceType< any >, subSectionIndex ?: number) : void {
    /**
     * This is needed due to how / when Angular detection cycle runs and is triggered.
     *
     * Angular change detection only checks object identity, not object content.
     * Therefore, inserts or removals are not detected.
     *
     * This makes the sub-section component ngOnChanges detect the portfolioContent property value change.
     */
    if (typeof (subSectionIndex) === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      subSectionType.portfolioContent = subSectionType.portfolioContent.slice();
      this.sectionDataAlertService.handleSaveButtonForAddRemovePortfolioContent(subSectionType);
    } else {
      const subSectionCloneType : InstanceType<any> = (subSectionType.experienceSubSectionClone)
        ? subSectionType.experienceSubSectionClone : subSectionType.educationSubSectionClone;
      subSectionCloneType[subSectionIndex].portfolioContent = subSectionCloneType[subSectionIndex].portfolioContent
        .slice();
    }
    // Update portfolio content modified map :
    this.sectionDataAlertService.updatePortfolioContentModifiedMap(subSectionType, subSectionIndex);
    // eslint-disable-next-line no-plusplus
    // eslint-disable-next-line no-param-reassign
    subSectionType.numberOfSelectedContent--;
    this.sectionDataAlertService.handleSaveButtonForAddRemovePortfolioContent(subSectionType, subSectionIndex);
  }

  /**
   * Method that handles removing any unsaved portfolio content from the view.
   *
   * @param subSectionType
   * @param subSectionIndex
   */
  public handleUnsavedPortfolioContent(subSectionType : InstanceType< any >, subSectionIndex ?: number) : void {
    if (typeof (subSectionIndex) === 'undefined') {
      if (subSectionType.numberOfSelectedContent >= 1 && !subSectionType.hasSaved) {
        subSectionType.portfolioContent.splice(
          (subSectionType.portfolioContent.length - subSectionType.numberOfSelectedContent),
          subSectionType.numberOfSelectedContent,
        );
      } else if (subSectionType.numberOfRemovedContent >= 1 && !subSectionType.hasSaved) {
        const sectionCloneType : InstanceType<any> = (subSectionType.aboutMeSubSectionClone)
          ? subSectionType.aboutMeSubSectionClone : subSectionType.customSubSectionClone;
        // eslint-disable-next-line no-param-reassign
        subSectionType.portfolioContent = sectionCloneType.portfolioContent;
      }
      /**
       * This is needed due to how / when Angular detection cycle runs and is triggered.
       *
       * Angular change detection only checks object identity, not object content.
       * Therefore, inserts or removals are not detected.
       *
       * This makes the sub-section component ngOnChanges detect the portfolioContent property value change.
       */
      // eslint-disable-next-line no-param-reassign
      subSectionType.portfolioContent = subSectionType.portfolioContent.slice();
    } else {
      // As we are using common method for handling unsaved portfolio content.
      // We need to type cast the subSectionType to the correct section type before performing any operation.
      const subSectionCloneType : InstanceType<any> = (subSectionType.experienceSubSectionClone)
        ? subSectionType.experienceSubSectionClone : subSectionType.educationSubSectionClone;
      if (subSectionType.numberOfSelectedContent >= 1 && !subSectionType.hasSaved) {
        subSectionCloneType[subSectionIndex].portfolioContent.splice(
          (subSectionCloneType[subSectionIndex].portfolioContent.length - subSectionType.numberOfSelectedContent),
          subSectionType.numberOfSelectedContent,
        );
      }
      /**
       * This is needed due to how / when Angular detection cycle runs and is triggered.
       *
       * Angular change detection only checks object identity, not object content.
       * Therefore, inserts or removals are not detected.
       *
       * This makes the sub-section component ngOnChanges detect the portfolioContent property value change.
       */
      subSectionCloneType[subSectionIndex].portfolioContent = subSectionCloneType[subSectionIndex].portfolioContent
        .slice();
    }
    // reset save state variables:
    // eslint-disable-next-line no-param-reassign
    subSectionType.numberOfSelectedContent = 0;
    // eslint-disable-next-line no-param-reassign
    subSectionType.numberOfRemovedContent = 0;
    // eslint-disable-next-line no-param-reassign
    subSectionType.removedPortfolioContent = [];
    this.sectionDataAlertService.resetPortfolioContentModifiedMap();
    // eslint-disable-next-line no-param-reassign
    subSectionType.hasSaved = false;
  }
}
